import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useParams } from '../../../contexts/RouterContext';
import ProjectGroupsModule from '../../../redux/project_grouping';
import { nuc } from '../../../utils';
import { isReleaseGroupScope, type ReportsV3Params } from '../types';

/**
 * Builds the preview URL for the vulnerability (security) report.
 * Targets the `/attribution/security` endpoints, which return HTML content
 * when `preview=true` (and render a PDF otherwise). Format is always PDF.
 *
 * @deprecated The Security/Vulnerability report is a legacy surface gated by
 * the `SECURITY_PDF_REPORTS` feature flag. This report format is no longer
 * supported and is being phased out — do not add new functionality to this
 * hook or extend its behaviour. New reporting work should target the
 * Remediation Guidance report (or successor) instead.
 */
export const useVulnerabilityPreviewURL = (): string => {
  const params = useParams<ReportsV3Params>();
  const isReleaseGroup = isReleaseGroupScope(params);
  const selectedReleaseId = useSelector(ProjectGroupsModule.selectors.getSelectedReleaseId);

  return useMemo(() => {
    if (isReleaseGroup) {
      const { projectGroupId, releaseId } = params;
      const resolvedReleaseId = releaseId ?? selectedReleaseId ?? 0;
      return `/api/project_group/${projectGroupId}/release/${resolvedReleaseId}/attribution/security?preview=true&format=PDF`;
    }

    const projectLocator = nuc(`${params.project_id}$${params.revision_id}`);
    return `/api/revisions/${projectLocator}/attribution/security?preview=true&format=PDF`;
  }, [isReleaseGroup, params, selectedReleaseId]);
};
